import { ApolloQueryResult, FetchResult } from '@apollo/client';
import Sentry from '@sentry/browser';
import { OrderLineItem } from 'components/pages/order-details';
import { InstallmentEditable } from 'components/registration/v2/types';
import api, { Response } from 'core/api';
import ApolloClient from 'core/apollo-client';
import CancelFees from 'core/gql/mutations/cancel-fees';
import CreateNote from 'core/gql/mutations/create-note';
import CreateOfflinePayment from 'core/gql/mutations/create-offline-payment';
import CreateRefundMutation from 'core/gql/mutations/create-refund';
import SendEmailMutation from 'core/gql/mutations/send-email-to-customer';
import UpdateInstallment from 'core/gql/mutations/update-installment';
import InstallmentsQuery from 'core/gql/queries/installments';
import TransactionGroupsQuery from 'core/gql/queries/list-transaction-groups';
import OrderQuery from 'core/gql/queries/show-order';

const tags = {
  feature: 'POST_REG_EDITS',
};

export async function getOrder<T>(
  orderNumber: string
): Promise<ApolloQueryResult<T>> {
  try {
    return await ApolloClient.query({
      query: OrderQuery,
      variables: { orderNumber },
      fetchPolicy: 'network-only',
    });
  } catch (e) {
    Sentry.captureMessage('Error fetching order', {
      extra: {
        orderNumber,
      },
    });
    throw new Error(`Error in getOrder: ${e}`);
  }
}

export async function getNotes<T>(orderId: number): Promise<Response<T>> {
  try {
    return await api.get<T>(`/orders/${orderId}/notes?include_audit_logs=true`);
  } catch (e) {
    Sentry.captureMessage('Error fetching notes for order', {
      extra: {
        orderId,
      },
    });
    throw new Error(`Error in getNotes: ${e}`);
  }
}

export async function cancelFees<T>(
  orderId: number,
  orderLineItemIds: number[],
  deleteRosterProfiles: boolean
): Promise<FetchResult<T>> {
  try {
    return await ApolloClient.mutate({
      mutation: CancelFees,
      variables: { orderId, orderLineItemIds, deleteRosterProfiles },
    });
  } catch (e) {
    Sentry.captureMessage('Error canceling fees', {
      extra: {
        orderId,
        orderLineItemIds,
        deleteRosterProfiles,
      },
    });
    throw new Error(`Error in cancelFees: ${e}`);
  }
}

export async function getTransactions<T>(
  orderId: number
): Promise<ApolloQueryResult<T>> {
  try {
    return await ApolloClient.query({
      query: TransactionGroupsQuery,
      variables: { externalId: orderId, externalType: 'order' },
      fetchPolicy: 'network-only',
    });
  } catch (e) {
    Sentry.captureMessage('Error fetching transactions', {
      extra: {
        orderId,
      },
    });
    throw new Error(`Error in getTransactions: ${e}`);
  }
}

export async function refundTransaction<T>(
  orderId: number,
  paymentId: number,
  amount: number,
  note: string,
  type: string
): Promise<FetchResult<T>> {
  try {
    return await ApolloClient.mutate({
      mutation: CreateRefundMutation,
      variables: { amount, paymentId, orderId, note, type },
    });
  } catch (e) {
    Sentry.captureMessage('Error refunding transaction', {
      extra: { orderId },
    });
    throw new Error(`Error in refundTransaction: ${e}`);
  }
}

export async function getInstallments<T>(
  orderId: number
): Promise<ApolloQueryResult<T>> {
  try {
    return await ApolloClient.query({
      query: InstallmentsQuery,
      variables: { orderId },
      fetchPolicy: 'network-only',
    });
  } catch (e) {
    Sentry.captureMessage('Error fetching installments', {
      extra: {
        orderId,
      },
    });
    throw new Error(`Error in getInstallments: ${e}`);
  }
}

export async function updateInstallment<T>(
  id: number,
  canceled: boolean,
  scheduledDate?: string
): Promise<FetchResult<T>> {
  try {
    return await ApolloClient.mutate({
      mutation: UpdateInstallment,
      variables: scheduledDate
        ? { id, canceled, scheduledDate }
        : { id, canceled },
    });
  } catch (e) {
    Sentry.captureMessage('Error canceling installment', { extra: { id } });
    throw new Error(`Error in cancelInstallment: ${e}`);
  }
}

export async function createNote(
  orderId: number,
  body: string
): Promise<FetchResult> {
  try {
    return await ApolloClient.mutate({
      mutation: CreateNote,
      variables: { orderId, body },
    });
  } catch (e) {
    Sentry.captureMessage('Error creating note', { extra: { orderId } });
    throw new Error(`Error in createNote: ${e}`);
  }
}

export async function createOfflinePayment<T>(
  orderId: number,
  amount: number,
  offlinePaymentMethodType: string,
  note: string
): Promise<FetchResult<T>> {
  try {
    return await ApolloClient.mutate({
      mutation: CreateOfflinePayment,
      variables: { orderId, amount, offlinePaymentMethodType, note },
    });
  } catch (e) {
    Sentry.captureMessage('Error creating offline payment', {
      extra: { orderId },
    });
    throw new Error(`Error in createOfflinePayment: ${e}`);
  }
}

export async function sendEmailToCustomer<T>(
  orderId: number,
  from: string,
  subject: string,
  message: string
): Promise<T> {
  try {
    const response = await ApolloClient.mutate({
      mutation: SendEmailMutation,
      variables: { orderId, from, subject, message },
    });
    return response.data.sendEmailToCustomer;
  } catch (e) {
    Sentry.captureMessage('Error sending email to customer', {
      extra: { orderId },
    });
    throw new Error(`Error in sendEmailToCustomer: ${e}`);
  }
}

export async function getPreviewLineItems<T>(
  orderId: number,
  canceledLineItems: OrderLineItem[],
  newLineItems: OrderLineItem[]
): Promise<Response<T>> {
  try {
    return api.post<T>(
      `/orders/${orderId}/preview_line_items`,
      {
        orderId,
        canceledLineItems,
        newLineItems,
      },
      undefined,
      false
    );
  } catch (e) {
    Sentry.captureException(e, {
      extra: { orderId, canceledLineItems, newLineItems },
      tags,
    });
    throw new Error(`Error in getPreviewLineItems: ${e}`);
  }
}

export async function updateLineItems<T>(
  orderId: number,
  canceledLineItems: OrderLineItem[],
  newLineItems: OrderLineItem[],
  installments: InstallmentEditable[],
  note: string
): Promise<Response<T>> {
  try {
    return api.post<T>(
      `/orders/${orderId}/update_line_items`,
      {
        orderId,
        canceledLineItems,
        newLineItems,
        note,
        installments,
      },
      undefined,
      false
    );
  } catch (e) {
    Sentry.captureException(e, {
      extra: { orderId, canceledLineItems, newLineItems, note, installments },
      tags,
    });
    throw new Error(`Error in updateLineItems: ${e}`);
  }
}
